import { useEffect, useRef, useState } from "react"
import { fetchLibrarySearch, LIBRARY_SEARCH_MIN_LENGTH, type LibrarySearchResults } from "@/lib/library-api"

/** Пауза перед запросом: печатают быстрее, чем отвечает сервер. */
const DEBOUNCE_MS = 250

const EMPTY_RESULTS: LibrarySearchResults = { places: [], assets: [], truncated: false }

export type LibrarySearchState = {
  query: string
  setQuery: (next: string) => void
  clear: () => void
  /** Запрос достаточно длинный: содержимое папки заменено выдачей. */
  active: boolean
  /** Введено что-то короче минимума — подсказываем, а не ищем. */
  tooShort: boolean
  loading: boolean
  failed: boolean
  /** Повторить тот же запрос после отказа сети. */
  retry: () => void
  results: LibrarySearchResults
}

/**
 * Поиск по всему проекту: строка ввода, debounce и отмена предыдущего запроса.
 * Живёт рядом с браузером Библиотеки, поэтому одинаково работает и на странице
 * раздела, и в модалке выбора кадра.
 */
export function useLibrarySearch(projectId: number): LibrarySearchState {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<LibrarySearchResults>(EMPTY_RESULTS)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  // Держим последний запрос отдельно от эффекта: при быстром наборе ответ на
  // устаревшую строку приходил бы поверх свежей выдачи.
  const requestRef = useRef<AbortController | null>(null)

  const trimmed = query.trim()
  const active = trimmed.length >= LIBRARY_SEARCH_MIN_LENGTH

  useEffect(() => {
    requestRef.current?.abort()
    if (!active) {
      requestRef.current = null
      setResults(EMPTY_RESULTS)
      setLoading(false)
      setFailed(false)
      return
    }

    const controller = new AbortController()
    requestRef.current = controller
    setLoading(true)
    setFailed(false)
    const timer = window.setTimeout(() => {
      fetchLibrarySearch(projectId, trimmed, { signal: controller.signal })
        .then((next) => {
          if (controller.signal.aborted) return
          setResults(next)
        })
        .catch(() => {
          if (controller.signal.aborted) return
          setResults(EMPTY_RESULTS)
          setFailed(true)
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false)
        })
    }, DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [projectId, trimmed, active, reloadKey])

  return {
    query,
    setQuery,
    clear: () => setQuery(""),
    active,
    tooShort: trimmed.length > 0 && !active,
    loading,
    failed,
    retry: () => setReloadKey((key) => key + 1),
    results,
  }
}
